/**
 * Wall time in the dealership's timezone, from the absolute instants the API speaks.
 *
 * The browser's own timezone is never used: a salesperson on a phone that thinks it is in Perth
 * must still see the Sydney dealership's day. Everything here takes the IANA zone from
 * `general.timezone` and goes through `Intl`, so no date library and no offset table is needed.
 */

/** A calendar date with no zone attached, `YYYY-MM-DD`: what the URL's `date` param carries. */
export type CalendarDate = string

/** The keys `general.opening_hours` is shaped with, indexed by `Date#getUTCDay()`. */
export const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

export type DayKey = (typeof DAY_KEYS)[number]

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/** `Intl.DateTimeFormat` is expensive to build and the calendar reads one zone over and over. */
const formatters = new Map<string, Intl.DateTimeFormat>()

function formatter(timeZone: string): Intl.DateTimeFormat {
  let cached = formatters.get(timeZone)
  if (!cached) {
    cached = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    formatters.set(timeZone, cached)
  }
  return cached
}

type WallClock = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

/** What a clock on the dealership's wall reads at `at`. */
function wallClock(at: Date, timeZone: string): WallClock {
  const parts = formatter(timeZone).formatToParts(at)
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value)
  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    hour: value('hour'),
    minute: value('minute'),
    second: value('second'),
  }
}

/** The zone's offset from UTC at `at`, in milliseconds. Positive east of Greenwich. */
function offsetMs(at: Date, timeZone: string): number {
  const wall = wallClock(at, timeZone)
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second)
  // The parts carry no milliseconds, so compare against a whole second of `at`.
  return asUtc - Math.floor(at.getTime() / 1000) * 1000
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** True for a string the rest of this module can be handed: `2026-09-11`, not `2026-9-11`. */
export function isCalendarDate(value: string): boolean {
  return DATE_PATTERN.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`))
}

/**
 * The absolute instant at which the dealership's clock reads `date` at `time` (`HH:MM` or
 * `HH:MM:SS`, as `opening_hours` gives it).
 *
 * The zone's offset depends on the instant, which is what is being solved for, so it is guessed
 * once from the naive UTC reading and corrected once. A second pass is enough for every real
 * zone: the correction only moves the instant by hours, and two offsets differ only inside the
 * hour a DST change moves. A wall time that a spring-forward skipped lands on the instant the
 * clock jumped to, which is the right answer for "when does the day start".
 */
export function instantAt(date: CalendarDate, time: string, timeZone: string): string {
  const [hour = '0', minute = '0', second = '0'] = time.split(':')
  const naive = Date.parse(
    `${date}T${pad(Number(hour))}:${pad(Number(minute))}:${pad(Number(second))}Z`,
  )
  const first = naive - offsetMs(new Date(naive), timeZone)
  const corrected = naive - offsetMs(new Date(first), timeZone)
  return new Date(corrected).toISOString()
}

/**
 * The `from`/`to` a request for one day of the dealership's calendar takes: midnight to midnight
 * in its zone, as absolute instants. `to` is the next day's midnight, matching the API's
 * touches-the-range rule, so a booking that runs to 23:59 is in and one that starts at the next
 * midnight is not.
 */
export function dayWindow(date: CalendarDate, timeZone: string): { from: string; to: string } {
  return {
    from: instantAt(date, '00:00:00', timeZone),
    to: instantAt(addDays(date, 1), '00:00:00', timeZone),
  }
}

/**
 * The `from`/`to` a request for one week of the dealership's calendar takes: the Monday's midnight
 * to the following Monday's midnight, in its zone. Seven days is well inside the API's caps — 62
 * days for `GET /bookings`, 90 for availability — so one range serves both.
 */
export function weekWindow(date: CalendarDate, timeZone: string): { from: string; to: string } {
  const monday = startOfWeek(date)
  return {
    from: instantAt(monday, '00:00:00', timeZone),
    to: instantAt(addDays(monday, 7), '00:00:00', timeZone),
  }
}

/** The seven dates of `date`'s week, Monday first: the week grid's columns. */
export function weekDays(date: CalendarDate): CalendarDate[] {
  const monday = startOfWeek(date)
  return Array.from({ length: 7 }, (_, index) => addDays(monday, index))
}

/** Today's date in the dealership's zone, which is not always today's date on the phone. */
export function today(timeZone: string, now: Date = new Date()): CalendarDate {
  const wall = wallClock(now, timeZone)
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}`
}

/** `2026-09-11` + 1 -> `2026-09-12`. Plain calendar arithmetic; no zone is involved. */
export function addDays(date: CalendarDate, days: number): CalendarDate {
  const at = new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000)
  return at.toISOString().slice(0, 10)
}

/** Which day of the trading week `date` is, as `opening_hours` names it. */
export function dayKey(date: CalendarDate): DayKey {
  return DAY_KEYS[new Date(`${date}T00:00:00Z`).getUTCDay()] as DayKey
}

/** The Monday of `date`'s week: where the date strip starts. */
export function startOfWeek(date: CalendarDate): CalendarDate {
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay()
  // Sunday (0) belongs to the week that started six days earlier, not the one starting tomorrow.
  return addDays(date, weekday === 0 ? -6 : 1 - weekday)
}

/** An instant as the dealership's clock reads it: `2026-09-11T03:00:00Z` -> `13:00` in Sydney. */
export function timeOfDay(instant: string, timeZone: string): string {
  const wall = wallClock(new Date(instant), timeZone)
  return `${pad(wall.hour)}:${pad(wall.minute)}`
}

/** An `opening_hours` wall time as the calendar prints it: `17:30:00` -> `17:30`. */
export function wallTime(time: string): string {
  return time.slice(0, 5)
}

/** Which calendar date, in the dealership's zone, an instant falls on. */
export function dateOf(instant: string, timeZone: string): CalendarDate {
  return today(timeZone, new Date(instant))
}

const DAY_LABEL = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'UTC',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})

/** `2026-09-11` -> `Fri 11 Sept`: the heading above the agenda. */
export function dayLabel(date: CalendarDate): string {
  return DAY_LABEL.format(new Date(`${date}T00:00:00Z`))
}

const DAY_NUMBER = new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', day: 'numeric' })
const DAY_MONTH = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
})
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

/**
 * The week the grid is showing: `7 – 13 Sept 2026`, or `28 Sept – 4 Oct 2026` when it straddles
 * two months. The month is named once when both ends share it, because saying it twice reads as
 * two separate dates rather than one range.
 */
export function weekRangeLabel(date: CalendarDate): string {
  const monday = startOfWeek(date)
  const sunday = addDays(monday, 6)
  const sameMonth = monday.slice(0, 7) === sunday.slice(0, 7)
  const from = (sameMonth ? DAY_NUMBER : DAY_MONTH).format(new Date(`${monday}T00:00:00Z`))
  return `${from} – ${DAY_MONTH_YEAR.format(new Date(`${sunday}T00:00:00Z`))}`
}

const WEEKDAY_LABEL = new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', weekday: 'narrow' })

/** `2026-09-11` -> `F`: the letter above a date in the strip. */
export function weekdayLetter(date: CalendarDate): string {
  return WEEKDAY_LABEL.format(new Date(`${date}T00:00:00Z`))
}

/** `2026-09-11` -> `11 Sept`: a date small enough to sit under a rail dot. */
export function dayMonth(date: CalendarDate): string {
  return DAY_MONTH.format(new Date(`${date}T00:00:00Z`))
}

/** `2026-09-11` -> `11`: the number in the strip. */
export function dayOfMonth(date: CalendarDate): string {
  return String(new Date(`${date}T00:00:00Z`).getUTCDate())
}

const WEEKDAY_SHORT = new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', weekday: 'short' })
const MONTH_LONG = new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', month: 'long' })

/** `2026-09-11` -> `Fri`: the three letters over a date in the prototype's strip. */
export function weekdayShort(date: CalendarDate): string {
  return WEEKDAY_SHORT.format(new Date(`${date}T00:00:00Z`))
}

/**
 * `09:44` when the instant falls on `day`, `Tue 09:12` when it falls on another one.
 *
 * What the record's stamps are written with, on the rail and against every move of a return
 * time. The weekday appears the moment a stamp leaves the day the booking is on, because a loan
 * handed over on Monday and pushed to Thursday has times on it that mean nothing without one.
 * `day` is given rather than taken from the clock, so a record does not rewrite itself overnight.
 */
export function timeOnDay(instant: string, timeZone: string, day: CalendarDate): string {
  const on = dateOf(instant, timeZone)
  const time = timeOfDay(instant, timeZone)
  return on === day ? time : `${weekdayShort(on)} ${time}`
}

/**
 * `2026-09-11` -> `Fri 11 September`: the prototype's day label. Assembled from parts rather
 * than formatted as one, because ICU puts a comma after the weekday on some builds and the
 * prototype has none.
 */
export function dayLabelLong(date: CalendarDate): string {
  const instant = new Date(`${date}T00:00:00Z`)
  return `${weekdayShort(date)} ${dayOfMonth(date)} ${MONTH_LONG.format(instant)}`
}

export function minutesBetween(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 60_000)
}

const MINUTES_IN_DAY = 24 * 60

/**
 * How long a window is, the way staff say it: `45 min`, `1 hr`, `1 hr 30 min`, `3 days`. A loan is
 * counted in whole days once it passes one, because "49 hr" is nobody's idea of a two-day loan.
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.max(minutes, 0)} min`
  if (minutes >= MINUTES_IN_DAY) {
    const days = Math.round(minutes / MINUTES_IN_DAY)
    return days === 1 ? '1 day' : `${days} days`
  }
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`
}
